import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SessionManager } from './SessionManager.js';
import { SessionStorage } from '../fetcher/SessionStorage.js';
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionManager] = useState(() => new SessionManager());
  const navigate = useNavigate();
  const location = useLocation();

  const applyStoredUser = useCallback(() => {
    const stored = new SessionStorage().getUserData();
    const authUser = SessionStorage.toAuthUser(stored);
    setUser(authUser);
    return authUser;
  }, []);

  const checkAuth = async () => {
    // Check if we were redirected here due to auth failure
    const wasRedirected = sessionStorage.getItem('auth_redirect') === 'true';
    
    if (wasRedirected) {
      // If redirected due to auth failure, clear user data to prevent redirect loops
      sessionStorage.removeItem('auth_redirect');
      setUser(null);
      setLoading(false);
      return;
    }
    
    // CRITICAL: Skip checkAuth if we're on the OAuth callback page
    // The OAuth callback will handle authentication directly via nhsLogin()
    // Calling fetchCurrentUser() here would race with the nhs-login POST request
    // and fail with 401 because the session hasn't been established yet
    if (location.pathname === '/callback') {
      console.log('On OAuth callback page, skipping premature auth check');
      setLoading(false);
      return;
    }
    
    // Always peek the server as the source of truth. This validates the cookie,
    // returns the user, and provides the server's lastTokenRefresh timestamp used
    // to schedule the next refresh. An expired/invalid token returns null, so the
    // user is correctly treated as signed out on load.
    console.log('Restoring session from server');
    const userData = await sessionManager.fetchCurrentUser();
    if (userData) {
      console.log('Successfully restored user session from server');
      applyStoredUser();

      // Initialize session management (schedules refresh off server lastTokenRefresh)
      sessionManager.initializeSession(userData);
    } else {
      console.log('No valid session found on server');
      setUser(null);
    }

    setLoading(false);
  };

  useEffect(() => {
    // Check if user is authenticated on mount
    // Note: We only run this once on mount to avoid unnecessary checks on every navigation
    // Auth state updates after mount are handled by explicit login/logout calls
    checkAuth();
    
    // Clean up on unmount
    return () => {
      // Clean up session manager
      sessionManager.cleanup();
    };
  }, []);

  useEffect(() => {
    // The SessionManager dispatches this when a refresh reveals the server
    // session is gone (token expired/invalidated). Sign the user out locally.
    const handleSessionExpired = () => {
      console.log('Session expired; signing out');
      sessionManager.logout();
      setUser(null);
      navigate('/login');
    };

    window.addEventListener('medprompt:session-expired', handleSessionExpired);
    return () => {
      window.removeEventListener('medprompt:session-expired', handleSessionExpired);
    };
  }, [navigate, sessionManager]);

  useEffect(() => {
    const handleUserDataUpdated = () => applyStoredUser();
    window.addEventListener('userDataUpdated', handleUserDataUpdated);
    window.addEventListener('clinicChanged', handleUserDataUpdated);
    return () => {
      window.removeEventListener('userDataUpdated', handleUserDataUpdated);
      window.removeEventListener('clinicChanged', handleUserDataUpdated);
    };
  }, [applyStoredUser]);

  const login = async (email, password) => {
    const response = await sessionManager.login(email, password);
    return response;
  };

  const nhsLogin = async (code) => {
    await sessionManager.nhsLogin(code);
    return applyStoredUser();
  };

  const nhsOnboard = async (email, code) => {
    await sessionManager.nhsOnboard(email, code);
    return applyStoredUser();
  };

  const verify2FA = async (email, verificationCode) => {
    await sessionManager.verify2FA(email, verificationCode);
    return applyStoredUser();
  };

  const signup = async (email, signUpVerificationCode, password) => {
    const response = await sessionManager.signup(email, signUpVerificationCode, password);

    // Don't set user data yet - verification is required after signup
    return response;
  };

  const logout = () => {
    sessionManager.logout();
    setUser(null);
    navigate('/login');
  };

  const value = {
    user,
    loading,
    login,
    nhsLogin,
    nhsOnboard,
    signup,
    verify2FA,
    logout,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};

export default AuthContext; 